import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Spinner,
  Form,
  FloatingLabel,
} from "react-bootstrap";
import {
  usePageQuery,
  useUpdatePageMutation,
} from "../../../graphql/page.hooks";
import { Formik, Form as FormikForm, Field, FormikProps } from "formik";
import ReactSelect from "../../inputs/select";
import { SelectOption } from "../../../types";

interface IChangePageTemplateModalProps {
  handleCloseModal: () => void;
  pageType: PAGE_TYPE;
  pageId: number;
}

export enum PAGE_TYPE {
  MEQR = "meqr",
  MEPAGE = "mepage",
}

interface IInitialValues {
  order: number;
  pageCategoryId: number;
}

const ChangePageTemplateModal: React.FC<IChangePageTemplateModalProps> = ({
  handleCloseModal,
  pageType,
  pageId,
}) => {
  const formRef = useRef<FormikProps<IInitialValues>>(null);
  const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([]);

  const [updatePage, { loading: updateLoading }] = useUpdatePageMutation();
  const { data: pageData, loading: loadingPageInfo } = usePageQuery({
    variables: { id: pageId },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!pageData) return;
    setCategoryOptions(
      pageData.page.pagePageCategory.map((item) => ({
        value: item.pageCategory.id,
        label: item.pageCategory.title,
      }))
    );
  }, [pageData]);

  const handleSubmit = async (values: IInitialValues) => {
    const positionType =
      pageType === PAGE_TYPE.MEQR ? "positionMeQr" : "position";
    if (pageData?.page?.pagePageCategory?.length > 0) {
      await updatePage({
        variables: {
          id: pageId,
          pageCategoryIds: [
            {
              pagePageCategoryId: pageData.page?.pagePageCategory.find(
                (item) => item.pageCategory.id === values.pageCategoryId
              ).id,
              pageCategoryId: values.pageCategoryId,
              [positionType]: values.order,
            },
          ],
        },
      });
    }

    handleCloseModal();
  };

  const initialValues: IInitialValues = {
    order: 0,
    pageCategoryId: 0,
  };

  const handleCategoryChange = (selectedOption: SelectOption | null) => {
    const optionValue = selectedOption ? selectedOption.value : 0;
    formRef.current.setFieldValue("pageCategoryId", optionValue);

    const orderValue =
      pageData?.page?.pagePageCategory.find(
        (item) => item.pageCategory.id === optionValue
      )[pageType === PAGE_TYPE.MEQR ? "positionMeQr" : "position"] || 0;

    formRef.current.setFieldValue("order", orderValue);
  };

  return (
    <Modal
      show
      onHide={handleCloseModal}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title>
          Choose {pageType === PAGE_TYPE.MEQR ? "ME-QR" : "ME-Page"} Order
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingPageInfo ? (
          <div className="text-center">
            <Spinner className="my-5" />
          </div>
        ) : pageData?.page?.pagePageCategory.length === 0 ? (
          <div className="text-center my-5">
            There are no categories assigned for this page
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            innerRef={formRef}
            enableReinitialize
          >
            {({ values }) => (
              <FormikForm className="w-100">
                <Field name="pageCategoryId">
                  {() => (
                    <ReactSelect
                      options={categoryOptions}
                      className="mb-3"
                      value={categoryOptions.find(
                        (option) => option.value === values.pageCategoryId
                      )}
                      onChange={handleCategoryChange}
                      placeholder="Select Category"
                      label="Category"
                    />
                  )}
                </Field>
                {values.pageCategoryId !== 0 && (
                  <FloatingLabel
                    controlId="floatingOrder"
                    label="Order"
                    className="mb-3"
                  >
                    <Field
                      name="order"
                      type="number"
                      placeholder="Order"
                      as={Form.Control}
                      min="0"
                    />
                  </FloatingLabel>
                )}

                <Row className="gx-2 mt-3">
                  <Col lg={6}>
                    <Button
                      variant="outline-dark"
                      onClick={handleCloseModal}
                      className="w-100 fw-normal mb-4 mb-lg-0 dark-hover"
                      disabled={updateLoading}
                    >
                      Cancel
                    </Button>
                  </Col>
                  <Col lg={6}>
                    <Button
                      variant="primary"
                      className="w-100 text-white"
                      disabled={updateLoading || values.pageCategoryId === 0}
                      type="submit"
                    >
                      Confirm {updateLoading && <Spinner />}
                    </Button>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ChangePageTemplateModal;
